/* eslint-disable jsx-a11y/anchor-is-valid */
import { type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@fpsm/cxy-components/Divider';

import { type Repub, REPUBS } from '@cx-modules/constants';

import {
  useAuthModal,
  type View,
  VIEW_FORGOT_PASSWORD,
  VIEW_LOGIN,
  VIEW_SIGNUP,
  VIEW_VERIFY_EMAIL,
  VIEW_VERIFY_PAYMENT } from
'~/providers/auth-modal-provider';

import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { Icons } from './components/icons';
import { LoginForm } from './components/LoginForm';
import { SignupForm } from './components/SignupForm';
import { SocialButtons } from './components/SocialButtons';
import { VerifyEmail } from './components/VerifyEmail';
import { VerifyPayment } from './components/VerifyPayment';
import styles from './styles/Authentication.module.css';

type InfoSectionProps = {
  resourcesUrl: string;
  view: View;
};

export const InfoLink = ({ repub }: {repub: Repub;}) =>
<p className={styles.infoLink}>
		For more information{' '}
		<a
    href={`${repub !== REPUBS.OC.name ? `/${repub.toLowerCase()}` : ''}/plus`}>

			click here
		</a>
	</p>;


const FeatureList = () =>
<ul className={styles.featureList}>
		{[
  'Positive Expected Value Bets',
  'AI Probabilities for each bet',
  'Public Betting Splits',
  'Trends and insights',
  'Custom Betslip'].
  map((feature, index) =>
  <li key={index}>
				<Icons.CircleCheckIcon />
				<span>{feature}</span>
			</li>
  )}
	</ul>;


export const InfoSection = ({
  children,
  resourcesUrl
}: PropsWithChildren<InfoSectionProps>) => {
  const { t } = useTranslation(['label']);
  return (
    <div className={styles.infoSection} data-testid="info-section">
			<div>
				<div className="flex items-center">
					<img
            alt="Oddschecker"
            src={`${resourcesUrl}/assets/img/oc-pro-logo.svg`} />

				</div>
				<h2 className="text-base/5 md:text-[32px]/[36px] font-futura font-extrabold text-oddschecker-green mt-8 md:mt-12">
					{t('CX_OCPLUS_AUTH_HEADER')}
				</h2>
				<p>{t('CX_OCPLUS_AUTH_TAGLINE')}</p>
				<FeatureList />
				{children}
			</div>
		</div>);

};

InfoSection.InfoLink = InfoLink;

const AuthSection = ({
  currentUrl,
  repub,
  view




}: {currentUrl: string;repub: Repub;view: View;}) => {
  const { close, openAtView } = useAuthModal();

  const isForgotPassword = view === VIEW_FORGOT_PASSWORD;
  const isSignup = view === VIEW_SIGNUP;
  const isVerifyingEmail = view === VIEW_VERIFY_EMAIL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;

  const { t } = useTranslation(['label']);

  return (
    <div className={styles.authSection} data-testid="auth-section">
			<div className="flex flex-col space-y-2">
				{!isVerifyingEmail &&
        <h2>
						{!isVerifyingPayment && <Icons.LockIcon />}
						{isForgotPassword ?
          t('CX_AUTH_FORGOT_PASSWORD') :
          isSignup ?
          t('CX_AUTH_SIGN_UP_TO_UNLOCK') :
          isVerifyingPayment ?
          t('CX_AUTH_ONE_MOMENT') :
          t('CX_AUTH_LOGIN_TO_UNLOCK')}
					</h2>
        }
				{isVerifyingPayment ?
        <VerifyPayment /> :
        isVerifyingEmail ?
        <VerifyEmail /> :
        isForgotPassword ?
        <ForgotPasswordForm repub={repub} /> :

        <>
						<SocialButtons currentUrl={currentUrl} repub={repub} view={view} />
						<Divider text="OR" />
						{isSignup ?
          <SignupForm close={close} openAtView={openAtView} repub={repub} /> :

          <LoginForm close={close} openAtView={openAtView} repub={repub} />
          }
					</>
        }
				{isSignup && !isForgotPassword &&
        <p className={styles.termsText}>
						{t('CX_SOCIAL_CONFIRM_AGE')}{' '}
						<a
            href={`${repub !== REPUBS.OC.name ? `/${repub.toLowerCase()}` : ''}/terms-and-conditions`}>

							{t('CX_AUTH_TS_AND_CS')}
						</a>{' '}
						and{' '}
						<a
            href={`${repub !== REPUBS.OC.name ? `/${repub.toLowerCase()}` : ''}/privacy`}>

							{t('CX_AUTH_PRIVACY_POLICY')}
						</a>
						.
					</p>
        }
				{(isForgotPassword || isSignup) &&
        <p className={styles.switchAuthText}>
						{isSignup ?
          `${t('CX_AUTH_ALREADY_HAVE_ACCOUNT')} ` :
          `${t('CX_AUTH_DONT_HAVE_ACCOUNT')} `}
						<a
            href={`${repub !== REPUBS.OC.name ? `/${repub.toLowerCase()}` : ''}/plus/auth/${isSignup ? 'login' : 'signup'}`}
            onClick={(e) => {
              e.preventDefault();
              openAtView(
                isForgotPassword ?
                VIEW_SIGNUP :
                isSignup ?
                VIEW_LOGIN :
                VIEW_SIGNUP
              );
            }}>

							{isSignup ? t('CX_AUTH_LOGIN') : t('CX_AUTH_SIGNUP')}
						</a>
					</p>
        }
			</div>
		</div>);

};

function Authentication({ children }: PropsWithChildren) {
  return (
    <div className={styles.authenticationContainer} data-testid="auth-modal">
			{children}
		</div>);

}

Authentication.AuthSection = AuthSection;
Authentication.InfoSection = InfoSection;

export { Authentication };